import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import {
  orgDashboardManageListingsRoute,
  orgDashboardPostAListingRoute,
} from '#/modules/orgDashboard/routing/orgDashboard.routes';
import { UserDashboardPostAListingPageLoadable } from '#/modules/userDashboard/pages/PostAListing/UserDashboardPostAListingPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import { SIGN_UP_TO_POST_URL_FOR_LOCALE } from '#/routing/route-constants';
import type { postAListingRoute } from '#/routing/routes';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';

export const handle = {
  variant: 'base',
  darkBackground: true,
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
}: HorizonAppRouteLoaderArgs<typeof postAListingRoute>) {
  const { user } = await apiIsomorphicFetchUserEnvironment({
    request,
    context,
  })();

  if (!user) {
    return idealistRedirect({ request, context })(
      SIGN_UP_TO_POST_URL_FOR_LOCALE[context.locale],
    );
  }

  if (user.orgs.length === 1) {
    const [org] = user.orgs;

    if (org.isAllowedToPost && !org.isFrozen) {
      return idealistRedirect({ request, context })(
        orgDashboardPostAListingRoute.with({ orgId: org.id }),
      );
    }

    return idealistRedirect({ request, context })(
      orgDashboardManageListingsRoute.with({ orgId: org.id }),
    );
  }

  return { orgs: user.orgs };
}

export default function UserDashboardPostAListingHorizonRoute({
  loaderData: { orgs },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  return (
    <RouteModule title={i18n('Post A Listing')}>
      <UserDashboardPostAListingPageLoadable orgs={orgs} />
    </RouteModule>
  );
}
